// Profile photo upload — shared by onboarding and the You tab.
//
// Every photo is re-encoded in the browser before upload:
//   - resized so the long edge is at most MAX_EDGE px (phone originals are
//     often 5–12 MB; this keeps uploads quick on mobile data)
//   - converted to JPEG
//   - stripped of all metadata. Phone photos can carry EXIF GPS coordinates,
//     and profile photos live at public S3 URLs — uploading originals could
//     reveal where someone took the photo (often at home). Drawing to a canvas
//     and re-encoding keeps only the pixels.
//
// If the browser can't decode the file (e.g. HEIC on Android Chrome), we fall
// back to uploading the original only when its type is one the API accepts.

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.85;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];

export class PhotoError extends Error {}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });
}

/** Resize + re-encode to JPEG without metadata. Returns the blob to upload. */
export async function preparePhoto(file: File): Promise<{ blob: Blob; contentType: string }> {
  if (!file.type.startsWith('image/') && file.type !== '') {
    throw new PhotoError('That file isn\'t a photo. Please choose an image.');
  }
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no canvas');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);  // browsers apply EXIF rotation here
    const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('encode failed');
    return { blob, contentType: 'image/jpeg' };
  } catch {
    if (ACCEPTED.includes(file.type)) return { blob: file, contentType: file.type };
    throw new PhotoError('We couldn\'t read that photo. Please choose a JPG or PNG.');
  }
}

export type PreparedPhoto = Awaited<ReturnType<typeof preparePhoto>>;

/** Prepare (resize, re-encode, strip metadata) then upload. See uploadPrepared. */
export async function uploadPhoto(file: File, token: string): Promise<string> {
  return uploadPrepared(await preparePhoto(file), token);
}

/**
 * Uploads an already-prepared photo straight to S3 via a presigned URL and
 * returns its public URL. The account must already exist (the API resolves the
 * uploader from the signed-in user). Does NOT save it to the profile.
 */
export async function uploadPrepared({ blob, contentType }: PreparedPhoto, token: string): Promise<string> {
  const urlRes = await fetch(`${API_BASE}/api/auth/photos/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ contentType }),
  });
  if (!urlRes.ok) throw new PhotoError('We couldn\'t upload your photo. Please try again.');
  const { uploadUrl, publicUrl } = await urlRes.json();
  if (!uploadUrl || !publicUrl) throw new PhotoError('We couldn\'t upload your photo. Please try again.');

  const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob });
  if (!putRes.ok) throw new PhotoError('We couldn\'t upload your photo. Check your connection and try again.');

  return publicUrl as string;
}
